import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSite } from '../hooks/useSites';
import { api } from '../api/client';
import { useToast } from '../hooks/useToast';
import '../styles/editor.css';

// ─── Types ───────────────────────────────────────────────────────────

interface DeployPreview {
  lastDeployedAt: string | null;
  neverDeployed: boolean;
  changes: {
    created: Array<{ type: string; title: string; contentKey: string }>;
    modified: Array<{ type: string; title: string; contentKey: string }>;
    summary: string;
  };
}

type PublishPhase = 'idle' | 'publishing' | 'success' | 'error' | 'timeout';
type PreviewPhase = 'idle' | 'building' | 'ready' | 'error';

// ─── Helpers ─────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const seconds = Math.floor((now - then) / 1000);

  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

function formatDurationMs(ms: number): string {
  const seconds = Math.round(ms / 1000);
  if (seconds < 1) return '<1s';
  return `${seconds}s`;
}

function formatBuildTimings(timings: Record<string, number>): string {
  // Build a readable pipeline string from step timings, excluding 'total'
  const stepLabels: Record<string, string> = {
    templateFetch: 'Template',
    templateExtract: 'Setup',
    contentFetch: 'Content',
    imageProcessing: 'Images',
    siteBuild: 'Build',
    mediaCopy: 'Media',
    s3Sync: 'Upload',
    dnsSetup: 'DNS',
    total: 'Total',
  };

  const steps = Object.entries(timings)
    .filter(([key]) => key !== 'total')
    .map(([key, ms]) => {
      const label = stepLabels[key] || key;
      return `${label}: ${formatDurationMs(ms)}`;
    });

  const totalMs = timings.total;
  if (totalMs) {
    steps.push(`Total: ${formatDurationMs(totalMs)}`);
  }

  return steps.join(' → ');
}

const POLL_INTERVAL = 3000;
const POLL_TIMEOUT = 5 * 60 * 1000; // 5 minutes

// ─── Component ───────────────────────────────────────────────────────

export default function PublishPage() {
  const { siteId } = useParams<{ siteId: string }>();
  const { site, loading, error, refresh, setSite } = useSite(siteId);
  const { addToast } = useToast();

  const [preview, setPreview] = useState<DeployPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [changesOpen, setChangesOpen] = useState(false);

  // Publish flow state
  const [publishPhase, setPublishPhase] = useState<PublishPhase>('idle');
  const [publishElapsed, setPublishElapsed] = useState(0);
  const [publishError, setPublishError] = useState<string | null>(null);
  const publishTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const publishPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const publishStartRef = useRef<number>(0);

  // Preview flow state
  const [previewPhase, setPreviewPhase] = useState<PreviewPhase>('idle');
  const [previewElapsed, setPreviewElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previewPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const previewStartRef = useRef<number>(0);
  const previewAutoOpened = useRef(false);

  // Build details expandable
  const [buildDetailsOpen, setBuildDetailsOpen] = useState(false);

  // ─── Deploy Preview (change summary) ────────────────────────────

  const fetchPreview = useCallback(async () => {
    if (!siteId) return;
    setPreviewLoading(true);
    try {
      const data = await api.getDeployPreview(siteId);
      setPreview(data);
      // Auto-open change summary if there are changes
      const total = data.changes.created.length + data.changes.modified.length;
      setChangesOpen(data.neverDeployed || total > 0);
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    fetchPreview();
  }, [fetchPreview]);

  // ─── Cleanup intervals on unmount ───────────────────────────────

  useEffect(() => {
    return () => {
      if (publishTimerRef.current) clearInterval(publishTimerRef.current);
      if (publishPollRef.current) clearInterval(publishPollRef.current);
      if (previewTimerRef.current) clearInterval(previewTimerRef.current);
      if (previewPollRef.current) clearInterval(previewPollRef.current);
    };
  }, []);

  // ─── On initial load: detect error or building state ────────────

  useEffect(() => {
    if (!site) return;

    // If the site is currently building (e.g. user refreshed mid-build), start polling
    if (site.status === 'building' && publishPhase === 'idle') {
      startPublishPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [site?.status]);

  // ─── Publish Polling Helpers ────────────────────────────────────

  const stopPublishPolling = useCallback(() => {
    if (publishTimerRef.current) {
      clearInterval(publishTimerRef.current);
      publishTimerRef.current = null;
    }
    if (publishPollRef.current) {
      clearInterval(publishPollRef.current);
      publishPollRef.current = null;
    }
  }, []);

  const startPublishPolling = useCallback(() => {
    publishStartRef.current = Date.now();
    setPublishPhase('publishing');
    setPublishElapsed(0);
    setPublishError(null);

    // Elapsed time counter
    publishTimerRef.current = setInterval(() => {
      setPublishElapsed(Math.floor((Date.now() - publishStartRef.current) / 1000));
    }, 1000);

    // Poll site status
    publishPollRef.current = setInterval(async () => {
      if (!siteId) return;

      // Check for timeout
      if (Date.now() - publishStartRef.current > POLL_TIMEOUT) {
        stopPublishPolling();
        setPublishPhase('timeout');
        return;
      }

      try {
        const updatedSite = await api.getSite(siteId);
        setSite(updatedSite);

        if (updatedSite.status === 'live') {
          stopPublishPolling();
          setPublishPhase('success');
          // Auto-refresh change summary
          fetchPreview();
        } else if (updatedSite.status === 'error') {
          stopPublishPolling();
          setPublishPhase('error');
          setPublishError(updatedSite.errorMessage || 'Something went wrong. Check your content and try again.');
        }
      } catch {
        // Polling error — keep trying
      }
    }, POLL_INTERVAL);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, stopPublishPolling, fetchPreview, setSite]);

  // ─── Preview Polling Helpers ────────────────────────────────────

  const stopPreviewPolling = useCallback(() => {
    if (previewTimerRef.current) {
      clearInterval(previewTimerRef.current);
      previewTimerRef.current = null;
    }
    if (previewPollRef.current) {
      clearInterval(previewPollRef.current);
      previewPollRef.current = null;
    }
  }, []);

  const startPreviewPolling = useCallback((url: string) => {
    previewStartRef.current = Date.now();
    setPreviewPhase('building');
    setPreviewElapsed(0);
    setPreviewUrl(url);
    previewAutoOpened.current = false;

    // Elapsed time counter
    previewTimerRef.current = setInterval(() => {
      setPreviewElapsed(Math.floor((Date.now() - previewStartRef.current) / 1000));
    }, 1000);

    // Poll preview status
    previewPollRef.current = setInterval(async () => {
      if (!siteId) return;

      // Check for timeout
      if (Date.now() - previewStartRef.current > POLL_TIMEOUT) {
        stopPreviewPolling();
        setPreviewPhase('error');
        return;
      }

      try {
        const updatedSite = await api.getSite(siteId);
        setSite(updatedSite);

        if (updatedSite.previewStatus === 'ready') {
          stopPreviewPolling();
          setPreviewPhase('ready');
          // Auto-open preview after a brief message
          if (!previewAutoOpened.current) {
            previewAutoOpened.current = true;
            setTimeout(() => {
              window.open(url, '_blank', 'noopener,noreferrer');
            }, 800);
          }
        } else if (updatedSite.previewStatus === 'error') {
          stopPreviewPolling();
          setPreviewPhase('error');
        }
      } catch {
        // Polling error — keep trying
      }
    }, POLL_INTERVAL);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, stopPreviewPolling, setSite]);

  // ─── Action Handlers ────────────────────────────────────────────

  const handlePublish = async () => {
    if (!siteId) return;
    try {
      await api.deploy(siteId);
      // Immediately update local site status so UI reacts before first poll
      setSite((prev) => prev ? { ...prev, status: 'building' } : prev);
      startPublishPolling();
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Publishing failed');
    }
  };

  const handleRetryPublish = async () => {
    setPublishPhase('idle');
    setPublishError(null);
    await handlePublish();
  };

  const handlePreview = async () => {
    if (!siteId) return;
    try {
      const result = await api.triggerPreview(siteId);
      if (result.previewUrl) {
        startPreviewPolling(result.previewUrl);
      }
    } catch (err) {
      addToast('error', err instanceof Error ? err.message : 'Preview failed');
    }
  };

  // ─── Loading / Error States ─────────────────────────────────────

  if (loading) {
    return (
      <div className="page">
        <div className="page-header"><h1>Publish</h1></div>
        <div className="loading-state"><div className="spinner" /></div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="page">
        <div className="page-header"><h1>Publish</h1></div>
        <div className="error-state"><p>{error || 'Site not found'}</p></div>
      </div>
    );
  }

  const totalChanges = preview
    ? preview.changes.created.length + preview.changes.modified.length
    : 0;

  const siteUrl = site.siteUrl || (site.customDomain ? `https://${site.customDomain}` : null);
  const isPublishing = publishPhase === 'publishing';
  const showPublishButton = publishPhase === 'idle';

  return (
    <div className="page">
      <div className="page-header">
        <h1>Publish Your Site</h1>
      </div>

      <div className="deploy-card">

        {/* ─── Error Banner (top of page, if site loaded with error) ─── */}
        {site.status === 'error' && publishPhase === 'idle' && (
          <div className="deploy-error-banner">
            <div className="deploy-error-banner-header">
              <span className="deploy-error-banner-icon">!</span>
              <strong>Your last publish ran into a problem</strong>
            </div>
            {site.errorMessage && (
              <p className="deploy-error-banner-message">{site.errorMessage}</p>
            )}
            <button
              className="btn btn-primary"
              onClick={handleRetryPublish}
            >
              Try Again
            </button>
          </div>
        )}

        {/* ─── Site URL ──────────────────────────────────────────── */}
        {siteUrl && publishPhase !== 'success' && (
          <div className="deploy-live">
            <p>
              Your site is at{' '}
              <a href={siteUrl} target="_blank" rel="noopener noreferrer">
                {siteUrl} ↗
              </a>
            </p>
          </div>
        )}

        {/* ─── Last Build Info ────────────────────────────────────── */}
        {site.lastDeployedAt && publishPhase === 'idle' && site.status !== 'error' && (
          <p className="deploy-last-build-info">
            Last published {timeAgo(site.lastDeployedAt)}
            {site.lastBuildDurationMs ? ` (took ${formatDurationMs(site.lastBuildDurationMs)})` : ''}
          </p>
        )}

        {/* ─── Change Summary (collapsible) ───────────────────── */}
        {!previewLoading && preview && publishPhase !== 'success' && (
          <div className="deploy-changes">
            {preview.lastDeployedAt && !site.lastDeployedAt && (
              <p className="deploy-last-time">
                Last published: <strong>{timeAgo(preview.lastDeployedAt)}</strong>
              </p>
            )}

            {preview.neverDeployed ? (
              <div className="deploy-changes-first">
                <p><strong>First publish</strong> — all your content will go live</p>
                {preview.changes.created.length > 0 && (
                  <details open={changesOpen} onToggle={(e) => setChangesOpen((e.target as HTMLDetailsElement).open)}>
                    <summary className="deploy-changes-toggle">
                      {preview.changes.created.length} item{preview.changes.created.length !== 1 ? 's' : ''} to publish
                    </summary>
                    <div className="deploy-changes-list">
                      {preview.changes.created.map((item) => (
                        <div key={item.contentKey} className="deploy-change-item">
                          <span className="deploy-change-badge deploy-change-badge--new">New</span>
                          <span className="deploy-change-type">{item.type}</span>
                          <span className="deploy-change-title">{item.title}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            ) : totalChanges === 0 ? (
              <p className="deploy-no-changes">Everything is up to date — no changes to publish</p>
            ) : (
              <details open={changesOpen} onToggle={(e) => setChangesOpen((e.target as HTMLDetailsElement).open)}>
                <summary className="deploy-changes-toggle">
                  {preview.changes.summary || `${totalChanges} change${totalChanges !== 1 ? 's' : ''}`}
                </summary>
                <div className="deploy-changes-detail">
                  {preview.changes.created.length > 0 && (
                    <div className="deploy-changes-list">
                      {preview.changes.created.map((item) => (
                        <div key={item.contentKey} className="deploy-change-item">
                          <span className="deploy-change-badge deploy-change-badge--new">New</span>
                          <span className="deploy-change-type">{item.type}</span>
                          <span className="deploy-change-title">{item.title}</span>
                        </div>
                      ))}
                    </div>
                  )}

                  {preview.changes.modified.length > 0 && (
                    <div className="deploy-changes-list">
                      {preview.changes.modified.map((item) => (
                        <div key={item.contentKey} className="deploy-change-item">
                          <span className="deploy-change-badge deploy-change-badge--updated">Updated</span>
                          <span className="deploy-change-type">{item.type}</span>
                          <span className="deploy-change-title">{item.title}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        )}

        {previewLoading && (
          <div className="deploy-changes-loading">
            <div className="spinner" />
            <span>Checking for changes…</span>
          </div>
        )}

        {/* ─── Publish Progress Card ──────────────────────────────── */}
        {isPublishing && (
          <div className="publish-progress-card">
            <div className="publish-progress-spinner">
              <div className="spinner" />
            </div>
            <div className="publish-progress-text">
              <p className="publish-progress-title">Publishing… {publishElapsed}s</p>
              <p className="publish-progress-hint">Building and uploading your site…</p>
            </div>
          </div>
        )}

        {/* ─── Publish Success ────────────────────────────────────── */}
        {publishPhase === 'success' && (
          <div className="publish-success-card">
            <div className="publish-success-check">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#065f46" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </div>
            <h3 className="publish-success-title">Your site is live!</h3>
            {site.lastBuildDurationMs && (
              <p className="publish-success-duration">Published in {formatDurationMs(site.lastBuildDurationMs)}</p>
            )}
            {siteUrl && (
              <a
                href={siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="publish-success-link"
              >
                View your site →
              </a>
            )}

            {/* Build details expandable */}
            {site.lastBuildTimings && Object.keys(site.lastBuildTimings).length > 0 && (
              <details
                className="publish-build-details"
                open={buildDetailsOpen}
                onToggle={(e) => setBuildDetailsOpen((e.target as HTMLDetailsElement).open)}
              >
                <summary className="publish-build-details-toggle">Build details</summary>
                <p className="publish-build-details-text">
                  {formatBuildTimings(site.lastBuildTimings)}
                </p>
              </details>
            )}

            <button
              className="btn btn-secondary publish-success-done"
              onClick={() => {
                setPublishPhase('idle');
                refresh();
              }}
            >
              Done
            </button>
          </div>
        )}

        {/* ─── Publish Error ──────────────────────────────────────── */}
        {publishPhase === 'error' && (
          <div className="publish-error-card">
            <div className="publish-error-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#991b1b" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <h3 className="publish-error-title">Publish failed</h3>
            {publishError && (
              <p className="publish-error-message">{publishError}</p>
            )}
            <button
              className="btn btn-primary"
              onClick={handleRetryPublish}
            >
              Try Again
            </button>
          </div>
        )}

        {/* ─── Publish Timeout ────────────────────────────────────── */}
        {publishPhase === 'timeout' && (
          <div className="publish-error-card">
            <h3 className="publish-error-title">This is taking longer than expected</h3>
            <p className="publish-error-message">
              Your site is probably still publishing in the background. Check back in a moment.
            </p>
            <button
              className="btn btn-primary"
              onClick={() => {
                setPublishPhase('idle');
                refresh();
              }}
            >
              Check status
            </button>
          </div>
        )}

        {/* ─── Actions ────────────────────────────────────────── */}
        {showPublishButton && site.status !== 'error' && (
          <div className="deploy-actions">
            <button
              className="btn btn-primary btn-lg"
              onClick={handlePublish}
            >
              Publish Site
            </button>

            {/* Preview button or inline preview state */}
            {previewPhase === 'idle' && (
              <button
                className="btn btn-secondary btn-lg"
                onClick={handlePreview}
              >
                Preview Before Publishing
              </button>
            )}

            {siteUrl && (
              <a
                href={siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary btn-lg"
              >
                View Live Site
              </a>
            )}
            <p className="deploy-hint">
              Publishing makes your latest changes visible to everyone who visits your site.
            </p>
            <p className="deploy-hint" style={{ marginTop: 4, fontSize: '0.85em', color: '#666' }}>
              Use &ldquo;Preview&rdquo; to check your changes before making them public.
            </p>
          </div>
        )}

        {/* ─── Preview Progress (inline, below actions) ───────────── */}
        {previewPhase === 'building' && (
          <div className="preview-progress-inline">
            <div className="spinner preview-progress-spinner" />
            <span>Building preview… {previewElapsed}s</span>
          </div>
        )}

        {previewPhase === 'ready' && previewUrl && (
          <div className="preview-ready-inline">
            <span className="preview-ready-label">Preview ready!</span>
            <a
              href={previewUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-secondary"
              onClick={() => {
                // Reset preview state after clicking
                setTimeout(() => setPreviewPhase('idle'), 500);
              }}
            >
              View Preview
            </a>
          </div>
        )}

        {previewPhase === 'error' && (
          <div className="preview-error-inline">
            <span>Preview failed — try again.</span>
            <button
              className="btn btn-secondary btn-sm"
              onClick={() => {
                setPreviewPhase('idle');
                handlePreview();
              }}
            >
              Try Again
            </button>
          </div>
        )}

        {/* ─── Explainer ──────────────────────────────────────── */}
        <div className="deploy-info">
          <h3>What happens when you publish?</h3>
          <ol>
            <li>All your content and images are gathered together</li>
            <li>Your site is built fresh with your latest changes</li>
            <li>The new version is uploaded and goes live</li>
            <li>Visitors see your updated site within seconds</li>
          </ol>
          <p style={{ marginTop: 12 }}>
            <Link to={`/sites/${siteId}/versions`} className="text-link">
              View previous versions
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
